import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { useLang } from "@/context/LangContext";

const Footer = () => {
  const { t, lang, toggle } = useLang();
  const wrapRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: wrapRef,
    // Track the wordmark from the moment it enters the viewport to the
    // moment its bottom rests at the bottom of the viewport.
    offset: ["start end", "end end"],
  });
  // Slide from left-of-center to perfectly centered. Spring smooths it out
  // so the motion feels gradual instead of sticky to scroll velocity.
  const xRaw = useTransform(scrollYProgress, [0, 1], ["-18%", "0%"]);
  const x = useSpring(xRaw, { stiffness: 40, damping: 22, mass: 0.6, restDelta: 0.0005 });

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="relative pt-24 pb-12 border-t border-white/[0.05]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Massive logo wordmark — fully visible with scroll-parallax shift */}
        <div
          ref={wrapRef}
          className="relative overflow-hidden mb-16 -mx-6 lg:-mx-10"
          data-testid="footer-wordmark-wrap"
        >
          <motion.div
            style={{ x, fontSize: "clamp(36px, 9vw, 200px)", WebkitTextStroke: "1px rgba(16,185,129,0.35)" }}
            className="font-display font-bold tracking-[-0.05em] text-transparent leading-[0.9] whitespace-nowrap select-none will-change-transform text-center"
            data-testid="footer-wordmark"
          >
            ProfitOperators
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-12 border-b border-white/[0.06]">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2 mb-5">
              <div className="h-7 w-7 rounded-md bg-gradient-to-br from-po-accent to-po-accent2 flex items-center justify-center text-[#04140C] font-display font-bold text-sm">
                P
              </div>
              <span className="font-display font-semibold text-white text-[15px]">ProfitOperators</span>
            </div>
            <p className="text-po-text2 text-[15px] leading-relaxed max-w-sm">{t.footer.blurb}</p>
          </div>

          <div className="md:col-span-4 grid grid-cols-2 gap-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-po-text3 mb-4">Navigate</div>
              <ul className="space-y-3 text-po-text2 text-[14px]">
                <li><button onClick={() => scrollTo("how")} className="hover:text-white transition" data-testid="footer-link-how">{t.nav.howItWorks}</button></li>
                <li><button onClick={() => scrollTo("benefits")} className="hover:text-white transition" data-testid="footer-link-benefits">{t.nav.benefits}</button></li>
                <li><button onClick={() => scrollTo("products")} className="hover:text-white transition" data-testid="footer-link-products">{t.nav.products}</button></li>
                <li><button onClick={() => scrollTo("faq")} className="hover:text-white transition" data-testid="footer-link-faq">{t.nav.faq}</button></li>
              </ul>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-po-text3 mb-4">Contact</div>
              <ul className="space-y-3 text-po-text2 text-[14px]">
                <li><a href="mailto:patrick@profitoperators.com" className="hover:text-white transition" data-testid="footer-email">patrick@profitoperators.com</a></li>
                <li><button onClick={() => scrollTo("contact")} className="hover:text-po-accent transition text-po-accent/90" data-testid="footer-cta">{t.nav.cta}</button></li>
              </ul>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.22em] text-po-text3 mb-4">Language</div>
            <button
              onClick={toggle}
              data-testid="footer-lang-toggle"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-[13px] uppercase tracking-[0.22em] text-po-text2 hover:text-white hover:border-po-accent/40 transition"
            >
              <span className={lang === "en" ? "text-white" : ""}>EN</span>
              <span className="text-white/20">/</span>
              <span className={lang === "fr" ? "text-white" : ""}>FR</span>
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] text-po-text3">
          <div>© 2026 ProfitOperators. {t.footer.rights}</div>
          <div className="tracking-[0.22em] uppercase text-po-accent/70">$0 Risk · $0 Upfront · We Win Together</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
